import { useEffect, useRef } from 'react';

/**
 * 弹窗外壳。规矩都在这里统一做，别让每个调用方各写一遍：
 * - 打开时锁 body 滚动，关闭后**焦点回到触发它的那个元素**
 * - Esc 关闭、点遮罩关闭（表单类弹窗由调用方决定要不要允许点遮罩关）
 * - 焦点进入弹窗内，Tab 循环限制在弹窗里，不跑到背景页面去
 *
 * 为什么登录也用它：登录跳转整页会把用户手上的活（刚跑完的报价单、正要下的单）冲掉，
 * 弹窗登录完就能接着做原来的动作。
 */
export function Modal({
  title, onClose, children, closeOnBackdrop = true, wide = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  closeOnBackdrop?: boolean;
  wide?: boolean;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // 焦点送进弹窗：优先第一个可输入的控件，否则弹窗本体
    const first = boxRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button:not([data-modal-close])');
    (first ?? boxRef.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = Array.from(boxRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])',
      ) ?? []);
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="modal-scrim"
      onMouseDown={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={'modal' + (wide ? ' wide' : '')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={boxRef}
      >
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="modal-x" data-modal-close onClick={onClose} aria-label="关闭">×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
